import * as React from 'react';
import FormInput from 'cl-form/FormInput';
import FormDialog, {SpecificFormDialogProps} from 'cl-dialogs/FormDialog';
import Button from '@material-ui/core/Button/Button';
import {Omit} from '@material-ui/core';
import {NumericOperator, OperatorType, StringOperator} from './redux/actions';
import {ExtendedWrappedFieldExternalProps, ExtendedWrappedFieldProps} from 'cl-form/FormFields';
import FormRadio from 'cl-form/FormRadio/FormRadio';
import FormSelect from 'cl-form/FormSelect/FormSelect';
import * as _ from 'lodash';
import {ValueType} from 'cl-form/validation/validators';
import {isNumericType} from './VirtualizedHelpers';
import {FieldWithName} from 'cl-form/FormSections/consts';
import TypedFormField from 'cl-form/TypedFormField/TypedFormField';

export interface DefaultEditDialogFormData {
  operator?: OperatorType;
  value?: ValueType;
}

export interface DefaultEditDialogProps
  extends Omit<
      SpecificFormDialogProps<DefaultEditDialogFormData, DefaultEditDialogFormData>,
      'formName' | 'initialValues' | 'variant' | 'onChange' | 'defaultValue'
    >,
    FieldWithName<any> {
  onChange: (value: ValueType, operator: OperatorType, props: DefaultEditDialogProps) => any;
  variant?: 'value' | 'value-and-operator'; // is only applicable if no options are passed
}

function enumToOptions(enum_) {
  return _.map(enum_, value => ({
    value,
  }));
}

const DefaultEditDialog: React.FunctionComponent<DefaultEditDialogProps> = props => {
  const {
    open,
    type,
    value,
    name,
    displayName,
    onChange,
    options,
    creatable,
    variant,
    orderBy,
    maxLength,
    maxValue,
    minValue,
    decimalScale,
    hideCreatedOption,
    ...otherProps
  } = props;
  let component: React.ComponentType<ExtendedWrappedFieldProps>;
  let fieldProps;
  if (type === 'shortString' || type === 'integer' || type === 'float' || type === 'select') {
    component = TypedFormField;
    fieldProps = {
      type,
      maxLength,
      maxValue,
      minValue,
      decimalScale,
      options,
      createOnBlur: true,
      creatable,
      hideCreatedOption,
      autoFocus: true,
    };
  } else if (options) {
    if (creatable) {
      component = FormSelect;
      fieldProps = {creatable, createOnBlur: true, clearable: true, hideCreatedOption, autoFocus: true};
    } else {
      component = FormRadio;
      fieldProps = {orderBy};
    }
  } else {
    component = FormInput;
    fieldProps = {autoFocus: true};
    if (type === 'number') {
      fieldProps = {...fieldProps, type: 'number'};
    }
    if (type === 'long') {
      fieldProps = {...fieldProps, type: 'number', inputProps: {min: 0, step: 1}};
    }
  }
  let fields: ExtendedWrappedFieldExternalProps<keyof DefaultEditDialogFormData>[] = [
    {component, name: 'value', props: {options, ...fieldProps}},
  ];
  const initialValues: Partial<DefaultEditDialogFormData> = {value};
  if (variant === 'value-and-operator' && !options) {
    if (isNumericType(type)) {
      fields = [{component: FormSelect, name: 'operator', props: {options: enumToOptions(NumericOperator)}}, ...fields];
      initialValues.operator = NumericOperator.GTE;
    } else if (type === 'text' || type === 'keyword' || type === 'shortString') {
      fields = [{component: FormSelect, name: 'operator', props: {options: enumToOptions(StringOperator)}}, ...fields];
      initialValues.operator = StringOperator.CONTAINS;
    }
  }
  return (
    <FormDialog<DefaultEditDialogFormData>
      open={open}
      onSubmit={({value, operator}) => onChange(value, operator, props)}
      formName={`editCellDialog-${name}`}
      initialValues={initialValues}
      title={`Edit ${displayName ? displayName : name}`}
      fields={fields}
      actions={
        <Button type="submit" color="primary">
          apply
        </Button>
      }
      {..._.omitBy(otherProps, value => value === undefined)}
    />
  );
};
export default DefaultEditDialog;
